import axios, { AxiosError, type AxiosInstance } from 'axios';
import { usePlatformAuthStore } from '@/features/platform-auth/store';

/**
 * In dev: VITE_API_URL is empty → baseURL becomes "/api/v1/admin" (relative).
 *         The Vite dev server proxies /api → http://localhost:4000.
 *
 * In prod: VITE_API_URL is the full API origin (e.g. https://api.jurivoxa.app).
 */
const API_URL = (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '');

export const platformApi: AxiosInstance = axios.create({
  baseURL: `${API_URL}/api/v1/admin`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'jurivoxa-web',
  },
});

// Attach Authorization header; prevent HTTP caching on GET requests
platformApi.interceptors.request.use((config) => {
  const token = usePlatformAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (config.method === 'get' || config.method === 'GET' || !config.method) {
    config.headers['Cache-Control'] = 'no-store';
    config.headers['Pragma'] = 'no-cache';
  }
  return config;
});

// Track in-flight refresh to coalesce concurrent 401s
let platformRefreshPromise: Promise<string> | null = null;

// Once a refresh has definitively failed, stop retrying
let platformAuthFailed = false;
usePlatformAuthStore.subscribe((state, prev) => {
  if (state.accessToken && state.accessToken !== prev.accessToken) {
    platformAuthFailed = false;
  }
});

async function doPlatformRefresh(): Promise<string> {
  if (platformRefreshPromise) return platformRefreshPromise;

  platformRefreshPromise = platformApi
    .post('/auth/refresh', {})
    .then((res) => {
      const data = res.data?.data as { accessToken: string };
      usePlatformAuthStore.getState().setAccessToken(data.accessToken);
      return data.accessToken;
    })
    .finally(() => {
      platformRefreshPromise = null;
    });

  return platformRefreshPromise;
}

// Auto-refresh on 401 (once); redirect to platform login on refresh failure
platformApi.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (platformAuthFailed) return Promise.reject(error);

    const original = error.config as (typeof error.config & { _retry?: boolean }) | undefined;
    if (
      error.response?.status === 401 &&
      original &&
      !original._retry &&
      !original.url?.includes('/auth/login') &&
      !original.url?.includes('/auth/refresh')
    ) {
      original._retry = true;
      try {
        const newToken = await doPlatformRefresh();
        if (original.headers) {
          original.headers.Authorization = `Bearer ${newToken}`;
        }
        return platformApi.request(original);
      } catch (retryErr: unknown) {
        const retryStatus = (retryErr as AxiosError)?.response?.status;
        if (!retryStatus || retryStatus === 401) {
          platformAuthFailed = true;
          usePlatformAuthStore.getState().clear();
          if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/platform/login')) {
            window.location.assign('/platform/login');
          }
        }
        return Promise.reject(retryErr);
      }
    }

    if (error.response && error.response.status >= 500) {
      import('sonner').then(({ toast }) => {
        toast.error('A server error occurred. Please try again or contact support if it persists.', {
          id: 'global-500-error'
        });
      });
    }

    return Promise.reject(error);
  },
);

/**
 * Helper: extract a usable error message from an Axios error.
 */
export function getPlatformApiErrorMessage(err: unknown, fallback = 'Something went wrong'): string {
  if (axios.isAxiosError(err)) {
    if (err.code === 'ERR_NETWORK' || !err.response) {
      return 'Cannot reach the Jurivoxa API. Is the API server running?';
    }
    const data = err.response?.data as { error?: { message?: string } } | undefined;
    return data?.error?.message ?? err.message ?? fallback;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}
